import { Injectable, inject } from '@angular/core';
import {
  BookBasketSessionRequestDto,
  BookWithSubscriptionRequestDto,
  EventSessionBlockResponseDto,
  EventSessionCategoryResponseDto,
  EventSessionContingentResponseDto,
  EventSessionForSaleResponseDto,
  EventSessionPriceResponseDto,
  PaymentModeResponseDto,
  SubscriptionBlockResponseDto,
  SubscriptionCategoryResponseDto,
  SubscriptionContingentResponseDto,
  SubscriptionDetailResponseDto,
  SubscriptionPriceResponseDto,
  SubscriptionResponseDto,
} from '@core/services/apiclient';
import { SalesBookingSummary, SalesEventTypeEnum } from '@sales/models/sales.model';
import { Observable } from 'rxjs';
import { SalesAbstractService } from './sales-abstract.service';
import { SalesSubscriptionService } from './sales-subscription.service';
import { SalesService } from './sales.service';

@Injectable({
  providedIn: 'root',
})
export class SalesFactoryService implements SalesAbstractService {
  salesEventsService = inject(SalesService);
  salesSubscriptionService = inject(SalesSubscriptionService);

  private _service: SalesService | SalesSubscriptionService;
  _type: SalesEventTypeEnum;
  public useFactory(type: SalesEventTypeEnum) {
    this._type = type;
    switch (this._type) {
      case SalesEventTypeEnum.Subscription:
      case SalesEventTypeEnum.Resubscription:
        this._service = this.salesSubscriptionService;
        break;
      case SalesEventTypeEnum.Event:
      default:
        this._service = this.salesEventsService;
        break;
    }
  }

  getEventSessions(
    channel: number,
  ): Observable<EventSessionForSaleResponseDto[] | SubscriptionResponseDto[]> {
    return this._service.getEventSessions(channel);
  }

  getSubscriptionDetails(sessionOrSubscriptionId: number): Observable<SubscriptionDetailResponseDto> {
    if (this._service instanceof SalesSubscriptionService) {
      return this._service.getSubscriptionDetails(sessionOrSubscriptionId);
    }
    return null;
  }

  getBlockType(sessionOrSubscriptionId: number): Observable<string> {
    return this._service.getBlockType(sessionOrSubscriptionId);
  }

  getPaymentMode(): Observable<PaymentModeResponseDto[]> {
    return this._service.getPaymentMode();
  }

  getBlocks(
    channelId: number,
    sessionOrSubscriptionId: number,
    contingentId: number,
    categoryId: number,
  ): Observable<EventSessionBlockResponseDto[] | SubscriptionBlockResponseDto[]> {
    return this._service.getBlocks(channelId, sessionOrSubscriptionId, contingentId, categoryId);
  }

  getCategories(
    channelId: number,
    sessionOrSubscriptionId: number,
    blockId: number,
    contingentId: number,
  ): Observable<EventSessionCategoryResponseDto[] | SubscriptionCategoryResponseDto[]> {
    return this._service.getCategories(channelId, sessionOrSubscriptionId, blockId, contingentId);
  }

  getPrices(
    channelId: number,
    sessionOrSubscriptionId: number,
    categoryId: number,
  ): Observable<EventSessionPriceResponseDto[] | SubscriptionPriceResponseDto[]> {
    return this._service.getPrices(channelId, sessionOrSubscriptionId, categoryId);
  }

  getContingents(
    sessionOrSubscriptionId: number,
    channelId: number,
    blockId?: number,
  ): Observable<EventSessionContingentResponseDto[] | SubscriptionContingentResponseDto[]> {
    return this._service.getContingents(sessionOrSubscriptionId, channelId, blockId);
  }

  bookSeats(
    sessionOrSubscription: BookBasketSessionRequestDto | BookWithSubscriptionRequestDto,
  ): Observable<SalesBookingSummary> {
    if (this._service instanceof SalesSubscriptionService) {
      return this._service.bookSeats(sessionOrSubscription as BookWithSubscriptionRequestDto);
    }
    return this._service.bookSeats(sessionOrSubscription as BookBasketSessionRequestDto);
  }

  unbookSeats(bookingIds: number[], basketId: number): Observable<boolean> {
    if (this._service instanceof SalesSubscriptionService) {
      return this._service.unbookSubscriptionSeats(bookingIds, basketId);
    }
    return this._service.unbookSeats(bookingIds, basketId);
  }
}
